import { ViewOnlyNotice } from '@/components/custom-roles/ViewOnlyNotice';
import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

type Position = { top: number; left: number };

const GAP = 12;

const Wrapper = styled.div<{ $stretch: boolean }>`
  display: inline-flex;

  > *:disabled {
    pointer-events: none;
  }

  ${({ $stretch }) =>
    $stretch &&
    `
      width: 100%;

      > * {
        width: 100%;
      }
    `}
`;

const Popover = styled.div<{ $position: Position | null }>`
  position: fixed;
  z-index: 10000;
  box-shadow: var(--box-shadow-soft);
  border-radius: var(--xsmall-border-radius);
  top: ${({ $position }) => $position?.top ?? 0}px;
  left: ${({ $position }) => $position?.left ?? 0}px;
  visibility: ${({ $position }) => ($position ? 'visible' : 'hidden')};
`;

const resolvePosition = (
  anchor: HTMLElement,
  notice: HTMLElement,
): Position => {
  const { top, bottom, left, right } = anchor.getBoundingClientRect();
  const { width, height } = notice.getBoundingClientRect();

  const besideLeft = left - GAP - width;
  const besideRight = right + GAP;

  const fitsLeft = besideLeft >= GAP;
  const fitsRight = besideRight + width <= window.innerWidth - GAP;

  let nextLeft = besideLeft;

  if (!fitsLeft) {
    nextLeft = fitsRight ? besideRight : (window.innerWidth - width) / 2;
  }

  const centered = top + (bottom - top) / 2 - height / 2;
  const highest = GAP;
  const lowest = window.innerHeight - height - GAP;

  return {
    top: Math.min(Math.max(centered, highest), Math.max(highest, lowest)),
    left: Math.max(nextLeft, GAP),
  };
};

export const ViewOnlyRestriction: React.FC<{
  children: ReactNode;
  stretch?: boolean;
}> = ({ children, stretch = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const hideTimeout = useRef(0);
  const wrapper = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const reposition = () => {
      if (wrapper.current && popover.current) {
        setPosition(resolvePosition(wrapper.current, popover.current));
      }
    };

    reposition();

    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    window.clearTimeout(hideTimeout.current);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeout.current = window.setTimeout(() => setIsVisible(false), 100);
  };

  return (
    <Wrapper
      $stretch={stretch}
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible &&
        createPortal(
          <Popover
            ref={popover}
            $position={position}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ViewOnlyNotice />
          </Popover>,
          document.body,
        )}
    </Wrapper>
  );
};

export const ViewOnlyRestrictionWhen: React.FC<{
  restricted: boolean;
  children: ReactNode;
  stretch?: boolean;
}> = ({ restricted, children, stretch }) =>
  restricted ? (
    <ViewOnlyRestriction stretch={stretch}>{children}</ViewOnlyRestriction>
  ) : (
    <>{children}</>
  );
